import React, { Reducer, useReducer, useEffect } from 'react';
import { Intent, Position, NumericInput } from '@blueprintjs/core';
import Button from '../../molecules/button';
import Tooltip from '../tooltip';
import ButtonGroup from '../button-group';
import { PaginatedParams } from '../../../libs/request';
import Icon from '../../atoms/icon';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';

interface PaginationProps extends PaginatedParams {
  total: number;
  isDisabled?: boolean;
  onPageChange: (page: number) => void;
}

interface InitialPaginationState {
  currentPage: number;
  limit: number;
  total: number;
}

interface PaginationState extends InitialPaginationState {
  pages: number[];
  showEndEllipsis: boolean;
  showStartEllipsis: boolean;
  totalPages: number;
  inputPage: number;
}

interface ChangePageAction {
  type: 'PAGE_CHANGE';
  page: number;
}

interface ChangeTotalAction {
  type: 'TOTAL_CHANGE';
  total: number;
}

interface ChangeLimitAction {
  type: 'LIMIT_CHANGE';
  limit: number;
}

interface ChangeInputPageAction {
  type: 'INPUT_PAGE_CHANGE';
  input: number;
}

type PaginationActions =
  | ChangePageAction
  | ChangeTotalAction
  | ChangeLimitAction
  | ChangeInputPageAction;

const PAGES_TO_SHOW = 3;
const PAGES_ON_EITHER_SIDE = 1;

const getState = ({
  currentPage,
  limit,
  total
}: InitialPaginationState): PaginationState => {
  const safeLimit =
    Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 10;
  const safeTotal =
    Number.isFinite(Number(total)) && Number(total) >= 0 ? Number(total) : 0;
  const safeCurrentPage =
    Number.isFinite(Number(currentPage)) && Number(currentPage) > 0
      ? Math.floor(Number(currentPage))
      : 1;
  let totalPages = 0;
  if (safeTotal > 0 && safeLimit > 0) {
    totalPages = Math.ceil(safeTotal / safeLimit);
  }
  let showStartEllipsis = false;
  let showEndEllipsis = false;
  // Create an array of pages to repeat in the pager control
  let startPage = 0;
  let endPage = 0;
  if (totalPages === 0) {
    startPage = 1;
    endPage = 1;
  } else if (totalPages <= PAGES_TO_SHOW) {
    // Less than PAGES_TO_SHOW total pages, so show all
    startPage = 1;
    endPage = totalPages;
  } else {
    if (safeCurrentPage <= PAGES_TO_SHOW - PAGES_ON_EITHER_SIDE) {
      // More than PAGINATION_THRESHOLD total pages so calculate start and end pages
      startPage = 1;
      endPage = PAGES_TO_SHOW;
      showEndEllipsis = true;
    } else if (safeCurrentPage + PAGES_ON_EITHER_SIDE >= totalPages) {
      // Current page approaching the total pages
      startPage = totalPages - (PAGES_TO_SHOW - 1);
      endPage = totalPages;
      showStartEllipsis = true;
    } else {
      // current page is somewhere in the middle
      startPage = safeCurrentPage - PAGES_ON_EITHER_SIDE;
      endPage = safeCurrentPage + PAGES_ON_EITHER_SIDE;
      showStartEllipsis = true;
      showEndEllipsis = true;
    }
  }
  const pages = Array.from(
    { length: endPage + 1 - startPage },
    (_, i) => startPage + i
  );
  // Too large or small currentPage
  let correctCurrentPage = safeCurrentPage;
  if (safeCurrentPage > totalPages && totalPages > 0) {
    correctCurrentPage = totalPages;
  }
  if (safeCurrentPage <= 0) {
    correctCurrentPage = 1;
  }
  return {
    currentPage: correctCurrentPage,
    pages,
    showEndEllipsis,
    showStartEllipsis,
    limit: safeLimit,
    total: safeTotal,
    totalPages,
    inputPage: correctCurrentPage
  };
};

const createNewPageChangeState = (
  state: PaginationState,
  action: ChangePageAction
): PaginationState => {
  const newState = getState({
    ...state,
    currentPage: action.page
  });
  return { ...newState, inputPage: newState.currentPage };
};

const reducer: Reducer<PaginationState, PaginationActions> = (
  state,
  action
) => {
  switch (action.type) {
    case 'PAGE_CHANGE':
      return createNewPageChangeState(state, action);
    case 'TOTAL_CHANGE': {
      return getState({
        ...state,
        total: action.total
      });
    }
    case 'LIMIT_CHANGE': {
      return getState({
        ...state,
        limit: action.limit
      });
    }
    case 'INPUT_PAGE_CHANGE': {
      return { ...state, inputPage: action.input };
    }
    default:
      throw new Error();
  }
};

export const Pagination = React.memo<PaginationProps>(
  ({ page = 1, total, limit = 10, isDisabled, onPageChange }): JSX.Element => {
    // Compose the state
    const [state, dispatch] = useReducer(
      reducer,
      {
        currentPage: page,
        total,
        limit,
        totalPages: 0,
        pages: [],
        showStartEllipsis: false,
        showEndEllipsis: false,
        inputPage: page
      },
      getState
    );

    const changePage = (page: number, shouldTrigger?: boolean) => {
      dispatch({
        type: 'PAGE_CHANGE',
        page
      });
      if (shouldTrigger !== false) {
        onPageChange(page);
      }
    };

    const handleInputSubmit = () => {
      const inputPageNumber = Number(state.inputPage);
      const desired = Number.isFinite(inputPageNumber)
        ? Math.floor(inputPageNumber)
        : 1;
      if (desired >= 1 && desired <= state.totalPages) {
        changePage(desired);
      }
    };
    // Update the pagination if total and limit change
    useEffect(() => {
      dispatch({
        type: 'TOTAL_CHANGE',
        total
      });
    }, [total]);
    useEffect(() => {
      dispatch({
        type: 'LIMIT_CHANGE',
        limit
      });
    }, [limit]);
    // Allow the parent to sync the page
    useEffect(() => {
      if (state.currentPage !== page) {
        changePage(page, false);
      }
    }, [page]);
    // Hide the pagination when there is only 1 page
    if (state.totalPages <= 1) {
      return null;
    }
    return (
      <div>
        <ButtonGroup>
          <Tooltip
            content="Previous Page"
            disabled={isDisabled || state.currentPage === 1}
            position={Position.TOP}
          >
            <Button
              icon={<Icon icon={faChevronLeft} />}
              disabled={isDisabled || state.currentPage === 1}
              onClick={() => changePage(Math.max(1, state.currentPage - 1))}
            />
          </Tooltip>
          <Button
            disabled={isDisabled || state.currentPage === 1}
            onClick={() => changePage(1)}
            intent={state.currentPage === 1 ? Intent.PRIMARY : Intent.NONE}
          >
            1
          </Button>
          {state.showStartEllipsis && state.pages[0] > 2 && (
            <Button disabled={true}>&#8230;</Button>
          )}
          {state.pages
            .filter((page) => page !== 1 && page !== state.totalPages)
            .map((page) => (
              <Button
                key={page}
                intent={
                  state.currentPage === page ? Intent.PRIMARY : Intent.NONE
                }
                disabled={isDisabled}
                onClick={() => changePage(page)}
              >
                {page}
              </Button>
            ))}
          {state.showEndEllipsis &&
            state.pages[state.pages.length - 1] < state.totalPages - 1 && (
              <Button disabled={true}>&#8230;</Button>
            )}
          <Button
            disabled={isDisabled || state.currentPage === state.totalPages}
            onClick={() => changePage(state.totalPages)}
            intent={
              state.currentPage === state.totalPages
                ? Intent.PRIMARY
                : Intent.NONE
            }
          >
            {state.totalPages}
          </Button>
          <Tooltip
            content="Next Page"
            disabled={isDisabled || state.currentPage === state.totalPages}
            position={Position.TOP}
          >
            <Button
              icon={<Icon icon={faChevronRight} />}
              disabled={isDisabled || state.currentPage === state.totalPages}
              onClick={() =>
                changePage(Math.min(state.currentPage + 1, state.totalPages))
              }
            />
          </Tooltip>
          <NumericInput
            value={state.inputPage}
            min={1}
            max={state.totalPages}
            disabled={isDisabled}
            allowNumericCharactersOnly={true}
            onValueChange={(value: number) =>
              dispatch({ type: 'INPUT_PAGE_CHANGE', input: value })
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                handleInputSubmit();
              }
            }}
            style={{ width: 60 }}
            className="ml-4"
          />
          <Button
            onClick={handleInputSubmit}
            disabled={
              isDisabled ||
              state.inputPage < 1 ||
              state.inputPage > state.totalPages
            }
            className="ml-4"
          >
            Go
          </Button>
        </ButtonGroup>
      </div>
    );
  }
);

export default Pagination;
